import * as v from "valibot";

const PublicUrlSchema = v.pipe(v.string(), v.url());
const HandoffPathSchema = v.pipe(
  v.string(),
  v.check((value) => value.startsWith("/"), "Path must start with /."),
);

// Vite client-side env. Source of truth for `import.meta.env` parsing in browser bundles.
// Bindings (D1, KV, ...) are not relevant here — keep this schema scoped to public VITE_* / DEV flags.
export const ClientEnvSchema = v.object({
  DEV: v.optional(v.boolean(), false),
  MODE: v.optional(v.string()),
  VITE_APP_ENV: v.optional(v.picklist(["dev", "staging", "prod"])),
  VITE_SENTRY_DSN: v.optional(v.string()),
  VITE_SENTRY_RELEASE: v.optional(v.string()),
  VITE_SENTRY_TRACES_SAMPLE_RATE: v.optional(v.string()),
  VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE: v.optional(v.string()),
  VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE: v.optional(v.string()),
  VITE_OPENFEATURE_FLAG_OVERRIDES: v.optional(v.string()),
  VITE_APP_ORIGIN: v.optional(PublicUrlSchema),
  VITE_API_BASE_URL: v.optional(PublicUrlSchema),
  VITE_PUBLIC_WEB_ORIGIN: v.optional(PublicUrlSchema),
  VITE_AUTH_APP_ORIGIN: v.optional(PublicUrlSchema),
  VITE_MOBILE_HANDOFF_PATH: v.optional(HandoffPathSchema),
  VITE_APP_STORE_URL: v.optional(PublicUrlSchema),
  VITE_PLAY_STORE_URL: v.optional(PublicUrlSchema),
});

export type ClientEnv = v.InferOutput<typeof ClientEnvSchema>;

export type ClientEnvParseResult =
  | { success: true; env: ClientEnv }
  | { success: false; env: ClientEnv; errorSummary: string };

// Apps should call this once at module load time to get a validated `clientEnv`.
// Keeping the Valibot dependency inside `domain` means apps do not need to declare it directly.
export function parseClientEnv(raw: unknown): ClientEnvParseResult {
  const parsed = v.safeParse(ClientEnvSchema, raw);
  if (parsed.success) {
    return { success: true, env: parsed.output };
  }
  const errorSummary = parsed.issues
    .map((issue) => `${issue.path?.map((p) => String(p.key)).join(".") ?? "?"}: ${issue.message}`)
    .join("; ");
  const fallback: ClientEnv = {
    DEV: Boolean((raw as { DEV?: unknown })?.DEV),
  };
  return { success: false, env: fallback, errorSummary };
}
